/**
 * J-code set matching helpers for sibling-order filtering.
 * @module siblingOrderFilter/jCodeMatching
 */
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { FilterJCodeInput } from "./types";

/**
 * Collects the distinct, defined `j_code` values from a list of J-code details.
 *
 * @param jCodes - The J-code detail entries to extract codes from
 * @returns A set of unique, non-empty `j_code` values
 */
export const toJCodeSet = (jCodes: FilterJCodeInput[]): Set<string> => {
  return new Set(
    jCodes
      .map((jCode) => jCode?.j_code)
      .filter((code): code is string => Boolean(code)),
  );
};

/**
 * Determines whether two lists of J-code details contain exactly the same set
 * of `j_code` values.
 *
 * The comparison is based on the distinct `j_code` values only (order and
 * duplicates are ignored); other fields on the entries are not considered.
 *
 * @param first - The first list of J-code details
 * @param second - The second list of J-code details
 * @returns `true` when both lists share the exact same set of `j_code` values,
 *   otherwise `false`
 */
export const areJCodesExactlySame = (
  first: FilterJCodeInput[],
  second: FilterJCodeInput[],
): boolean => {
  const firstCodes = toJCodeSet(first);
  const secondCodes = toJCodeSet(second);
  if (firstCodes.size !== secondCodes.size) {
    return false;
  }
  return [...firstCodes].every((code) => secondCodes.has(code));
};

/**
 * Whether an auth status subjects a J-code to a bounded authorization window
 * with a finite number of remaining visits (`auth_on_file` / `auth_by_risa`).
 *
 * @param authStatus - The J-code's authorization status
 * @returns `true` for the window-bound statuses, otherwise `false`
 */
export const isAuthWindowStatus = (authStatus?: string): boolean =>
  authStatus === MedicalPaOrdersAuthStatus.AuthOnFile ||
  authStatus === MedicalPaOrdersAuthStatus.AuthByRISA;
